import React, { useState, useEffect, useCallback, useRef } from 'react';
import { MessageSquare } from 'lucide-react';
import ManagementPage from '../../Common/components/common/ManagementPage';
import { useToast } from '../../Common/context/ToastContext';
import axios from 'axios';

const baseUrl = process.env.REACT_APP_API || '';

function parseDate(s) {
  const [y, m, d] = s.split('.').map(Number);
  return new Date(y, m - 1, d);
}

function fmtDate(d) {
  return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`;
}

function fmtApiDate(d) {
  return `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}`;
}

function fmtAligoDate(val) {
  if (!val) return '-';
  const s = String(val).trim();
  if (s.includes('-') || s.includes(':')) return s;
  if (s.length >= 14) {
    return `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)} ${s.slice(8, 10)}:${s.slice(10, 12)}:${s.slice(12, 14)}`;
  }
  return s;
}

function getPhone(item) {
  return item?.dest || item?.phone || item?.receiver || item?.mobile || '-';
}

function getMsgType(item) {
  return item?.msg_type || item?.type || 'MMS';
}

function getMsgId(item) {
  const v = item?.mid || item?.msg_id || item?.id;
  return v != null ? String(v) : '-';
}

function getSendDate(item) {
  return fmtAligoDate(item?.reg_date || item?.senddate || item?.send_date || item?.created_at || '');
}

function isSuccess(item) {
  if (item?.fail_count != null) return Number(item.fail_count) === 0;
  const s = item?.status ?? item?.result_code ?? item?.res_code;
  return s === 1 || s === '1' || s === 'success';
}


function SmsHistory() {
  const { showToast } = useToast();
  const showToastRef = useRef(showToast);
  showToastRef.current = showToast;

  const [startDate, setStartDate] = useState(fmtDate(new Date(new Date().getFullYear(), new Date().getMonth(), 1)));
  const [endDate, setEndDate] = useState(fmtDate(new Date()));

  const [remain, setRemain] = useState(null);
  const [remainLoading, setRemainLoading] = useState(true);

  const [rows, setRows] = useState([]);
  const [hasNext, setHasNext] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);

  const loadingMoreRef = useRef(false);
  const infiniteScrollRef = useRef(null);
  const pageRef = useRef(1);
  const hasNextRef = useRef(false);
  pageRef.current = page;
  hasNextRef.current = hasNext;

  const getApiParams = useCallback(
    (pageNum, pageSize = 30) => {
      const sd = parseDate(startDate);
      const ed = parseDate(endDate);
      const limitDay = Math.max(0, Math.round((ed - sd) / (1000 * 60 * 60 * 24)));
      return { page: pageNum, page_size: pageSize, start_date: fmtApiDate(ed), limit_day: limitDay };
    },
    [startDate, endDate],
  );

  const fetchRemain = useCallback(async () => {
    setRemainLoading(true);
    try {
      const { data } = await axios.get(baseUrl + '/api/admin/sms/remain', { withCredentials: true });
      if (data?.code === 1000) setRemain(data.data);
    } catch (_) {}
    finally { setRemainLoading(false); }
  }, []);

  const fetchList = useCallback(
    async (pageNum = 1, append = false) => {
      if (append && loadingMoreRef.current) return;
      if (!append) { setLoading(true); setHasNext(false); }
      else { loadingMoreRef.current = true; setLoadingMore(true); }

      try {
        const { data } = await axios.get(baseUrl + '/api/admin/sms/history', {
          params: getApiParams(pageNum),
          withCredentials: true,
        });
        if (data?.code !== 1000) {
          if (!append) { setRows([]); showToastRef.current(data?.msg || '문자내역 조회에 실패했습니다.', 'error'); }
          return;
        }
        const d = data.data || {};
        const list = Array.isArray(d.list) ? d.list : [];
        const nextYn = d.next_yn === 'Y';
        if (append) setRows((prev) => [...prev, ...list]);
        else { setRows(list); }
        setHasNext(nextYn);
        setPage(pageNum);
      } catch (e) {
        if (!append) { setRows([]); showToastRef.current('문자내역 조회 중 오류가 발생했습니다.', 'error'); }
      } finally {
        setLoading(false);
        loadingMoreRef.current = false;
        setLoadingMore(false);
      }
    },
    [getApiParams],
  );

  useEffect(() => { fetchRemain(); }, [fetchRemain]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchList(1, false); }, [startDate, endDate]);

  useEffect(() => {
    let observer = null;
    const id = requestAnimationFrame(() => {
      const el = infiniteScrollRef.current;
      if (!el) return;
      observer = new IntersectionObserver(
        (entries) => {
          if (!entries[0]?.isIntersecting || loadingMoreRef.current || !hasNextRef.current) return;
          fetchList(pageRef.current + 1, true);
        },
        { rootMargin: '100px', threshold: 0 },
      );
      observer.observe(el);
    });
    return () => { cancelAnimationFrame(id); if (observer) observer.disconnect(); };
  }, [fetchList, rows.length]);

  const setQuickPeriod = (type) => {
    const today = new Date();
    const ce = parseDate(endDate);
    const cs = parseDate(startDate);
    let ns, ne;
    switch (type) {
      case '당일': ns = ne = today; break;
      case '전일': ne = new Date(ce); ne.setDate(ne.getDate() - 1); ns = new Date(ne); break;
      case '당월': ns = new Date(today.getFullYear(), today.getMonth(), 1); ne = today; break;
      case '전월':
        ns = new Date(cs.getFullYear(), cs.getMonth() - 1, 1);
        ne = new Date(cs.getFullYear(), cs.getMonth(), 0);
        break;
      default: return;
    }
    setStartDate(fmtDate(ns));
    setEndDate(fmtDate(ne));
  };

  const cntVal = (key) => (remain?.[key] != null ? Number(remain[key]).toLocaleString() + '건' : '-');

  return (
    <ManagementPage
      hideSearch={true}
      hideExcelButton={true}
      hideDetail={true}
      emptyStateIcon={<MessageSquare size={32} className="opacity-20" />}
      filters={
        <div className="flex flex-col gap-3 w-full">
          {/* 기간 필터 */}
          <div className="flex items-center gap-3 flex-wrap">
            <div className="h-[40px] flex items-center gap-1.5 bg-white px-3 rounded-lg border border-[#e5e8eb] focus-within:border-[#0064ff] transition-all">
              <input
                type="date"
                value={startDate.replace(/\./g, '-')}
                onChange={(e) => setStartDate(e.target.value.replace(/-/g, '.'))}
                className="bg-transparent border-none outline-none text-[14px] text-[#191f28] tabular-nums py-0 h-full"
              />
              <span className="text-[#8b95a1] text-[12px]">~</span>
              <input
                type="date"
                value={endDate.replace(/\./g, '-')}
                onChange={(e) => setEndDate(e.target.value.replace(/-/g, '.'))}
                className="bg-transparent border-none outline-none text-[14px] text-[#191f28] tabular-nums py-0 h-full"
              />
            </div>
            <div className="flex gap-1">
              {['당일', '전일', '당월', '전월'].map((type) => (
                <button
                  key={type}
                  onClick={() => setQuickPeriod(type)}
                  className="px-2.5 py-1.5 bg-white border border-[#e5e8eb] rounded-lg text-[12px] font-bold text-[#4e5968] hover:bg-[#f2f4f6] leading-none h-[40px] flex items-center"
                >
                  {type}
                </button>
              ))}
            </div>
          </div>

          {/* 통계 카드 */}
          <div className="grid grid-cols-4 gap-3">
            {[
              { label: 'MMS 잔여건수', key: 'MMS_CNT', color: '#0064ff' },
              { label: 'SMS 잔여건수', key: 'SMS_CNT', color: '#00b493' },
              { label: 'LMS 잔여건수', key: 'LMS_CNT', color: '#f59e0b' },
            ].map(({ label, key, color }) => (
              <div key={key} className="bg-white rounded-xl border border-[#e0e0e0] shadow-sm px-4 py-3">
                <div className="text-[11px] text-[#8b95a1] mb-1">{label}</div>
                <div className="text-[15px] font-bold tabular-nums" style={{ color }}>
                  {remainLoading ? '-' : cntVal(key)}
                </div>
              </div>
            ))}
            <div className="bg-white rounded-xl border border-[#e0e0e0] shadow-sm px-4 py-3">
              <div className="text-[11px] text-[#8b95a1] mb-1">기간 내 발송건수</div>
              <div className="text-[15px] font-bold tabular-nums text-[#191f28]">
                {loading ? '-' : `${rows.length.toLocaleString()}건${hasNext ? ' 이상' : ''}`}
              </div>
            </div>
          </div>
        </div>
      }
      leftActions={
        <div className="text-[14px] font-bold text-[#191f28]">
          총 {loading ? '-' : rows.length.toLocaleString()}건{hasNext ? ' 이상' : ''}
        </div>
      }
      tableHeaders={
        <>
          <th className="px-3 py-1 w-[160px] text-[12px]">발송일시</th>
          <th className="px-3 py-1 w-[100px] text-[12px]">메시지ID</th>
          <th className="px-3 py-1 w-[130px] text-[12px]">수신번호</th>
          <th className="px-3 py-1 w-[80px] text-[12px]">타입</th>
          <th className="px-3 py-1 w-[80px] text-[12px]">결과</th>
          <th className="px-3 py-1 w-[80px] text-[12px]">테스트</th>
        </>
      }
      data={rows}
      infiniteScrollRef={infiniteScrollRef}
      renderRow={(item) => (
        <>
          <td className="px-3 py-1 w-[160px] text-[12px] text-[#4e5968] whitespace-nowrap tabular-nums">
            {getSendDate(item)}
          </td>
          <td className="px-3 py-1 w-[100px] text-[12px] text-[#4e5968] whitespace-nowrap tabular-nums">
            {getMsgId(item)}
          </td>
          <td className="px-3 py-1 w-[130px] text-[12px] text-[#4e5968] whitespace-nowrap tabular-nums">
            {getPhone(item)}
          </td>
          <td className="px-3 py-1 w-[80px] text-[12px] text-[#4e5968] whitespace-nowrap">
            {getMsgType(item)}
          </td>
          <td className={`px-3 py-1 w-[80px] text-[12px] font-semibold whitespace-nowrap ${isSuccess(item) ? 'text-[#00b493]' : 'text-[#b91c1c]'}`}>
            {isSuccess(item) ? '성공' : '실패'}
          </td>
          <td className="px-3 py-1 w-[80px] text-[12px] whitespace-nowrap text-[#4e5968]">
            {item?.testmode_yn === 'Y' ? 'Y' : '-'}
          </td>
        </>
      )}
    />
  );
}

export default SmsHistory;
